import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, X, ImageIcon, Loader2, Star, GripVertical, Eye } from 'lucide-react'
import { renderMarkdown } from '@/lib/markdown'
import {
  api, errorMessage, imageDisplayUrl,
  type AdminProduct, type ProductCreateInput,
  type PolicyTemplateRow, type PolicyTemplateType,
  type DictItem, type CoverTagColor,
  COVER_TAG_COLORS,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { fmtMoney } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { PageError, PageLoading } from './Stats'

// Group dict_items by dict_key, dropping inactive rows. Used to populate the
// editor's variant/cat/brand selects from a single API fetch.
type DictGroups = Record<string, DictItem[]>
function groupActiveDict(items: DictItem[]): DictGroups {
  const out: DictGroups = {}
  for (const r of items) {
    if (r.active !== 1) continue
    ;(out[r.dictKey] ??= []).push(r)
  }
  return out
}

export function ProductsScreen() {
  const toast = useToast()
  const { t } = useI18n()
  const [products, setProducts] = useState<AdminProduct[] | null>(null)
  const [dict, setDict] = useState<DictGroups>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<Editing | null>(null)
  const [deleting, setDeleting] = useState<AdminProduct | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  function load() {
    setBusy(true); setError(null)
    // Dict is best-effort: a failure here just leaves the selects with their
    // legacy/empty options, the product list still loads.
    api.listDict().then((r) => setDict(groupActiveDict(r.items))).catch(() => {})
    api.listProducts().then((r) => setProducts(r.products))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }

  useEffect(() => { load() }, [])

  async function toggleActive(p: AdminProduct) {
    try {
      await api.patchProduct(p.id, { active: !p.active })
      setProducts((prev) => prev?.map((x) => (x.id === p.id ? { ...x, active: !p.active } : x)) ?? null)
      toast.success(`${p.sku}`, p.active ? t('products.toast.disabled') : t('products.toast.enabled'))
    } catch (e) {
      toast.error(t('products.toast.updateFailed'), errorMessage(e))
    }
  }

  async function confirmDelete() {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await api.deleteProduct(deleting.id)
      setProducts((prev) => prev?.filter((x) => x.id !== deleting.id) ?? null)
      toast.success(`${deleting.sku}`, t('products.toast.deleted'))
      setDeleting(null)
    } catch (e) {
      toast.error(t('products.toast.deleteFailed'), errorMessage(e))
    } finally {
      setDeleteBusy(false)
    }
  }

  async function save(form: Editing) {
    // Compose metadata from the editor. We start with metadataExtra (which has
    // legacy fields stripped during toEditing) and overwrite the keys we own.
    const metadata: Record<string, unknown> = { ...form.metadataExtra }
    const setOrDelete = (key: string, val: unknown) => {
      if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) delete metadata[key]
      else metadata[key] = val
    }

    // 多选分类：写 metadata.cats (string[])。同时清掉旧的 metadata.cat
    // 单值字段，避免新旧两份数据共存。如果以后还要兼容老客户端读 cat，
    // 在这里同步写 cats[0]，但目前没必要。
    delete (metadata as Record<string, unknown>).cat
    setOrDelete('cats',     form.cats.length > 0 ? form.cats : null)
    setOrDelete('featured', form.featured ? true : null)

    // Detail-page model.
    setOrDelete('brand',          form.brand.trim() || null)
    setOrDelete('variant',        form.variant || null)
    setOrDelete('email_required', form.emailRequired === 'never' ? null : form.emailRequired)
    setOrDelete('images',         form.images.filter((u) => !!u))
    setOrDelete('tags',           form.tags.filter((t) => t.label.trim()).map((t) => ({ label: t.label.trim(), color: t.color })))
    const subtitleI18n: Record<string, string> = {}
    if (form.subtitleZh.trim()) subtitleI18n.zh = form.subtitleZh.trim()
    if (form.subtitleEn.trim()) subtitleI18n.en = form.subtitleEn.trim()
    setOrDelete('subtitle_i18n', Object.keys(subtitleI18n).length > 0 ? subtitleI18n : null)
    setOrDelete('price_orig_cny_cents', inputTextToCents(form.priceCnyOrigInput))
    setOrDelete('price_orig_usd_cents', inputTextToCents(form.priceUsdOrigInput))
    const descMdI18n: Record<string, string> = {}
    if (form.descMdZh.trim()) descMdI18n.zh = form.descMdZh
    if (form.descMdEn.trim()) descMdI18n.en = form.descMdEn
    setOrDelete('description_md_i18n', Object.keys(descMdI18n).length > 0 ? descMdI18n : null)
    const refs: Record<string, string> = {}
    if (form.policyDelivery) refs.delivery = form.policyDelivery
    if (form.policyAddress)  refs.address  = form.policyAddress
    if (form.policyNotice)   refs.notice   = form.policyNotice
    setOrDelete('policy_refs', Object.keys(refs).length > 0 ? refs : null)

    // Top-up options: only emit when variant === 'topup' AND there's at least one row.
    if (form.variant === 'topup' && form.topupOptions.length > 0) {
      metadata.topup_options = form.topupOptions
        .filter((o) => Number.isFinite(o.months) && o.months > 0)
        .map((o) => ({
          months: o.months,
          price_cny_cents: inputTextToCents(o.priceCnyInput),
          price_usd_cents: inputTextToCents(o.priceUsdInput),
          price_orig_cny_cents: inputTextToCents(o.priceCnyOrigInput),
          price_orig_usd_cents: inputTextToCents(o.priceUsdOrigInput),
        }))
    } else {
      delete metadata.topup_options
    }

    setOrDelete('sold_count', Number.isFinite(form.soldCount) && form.soldCount > 0 ? form.soldCount : null)
    setOrDelete('stock',      Number.isFinite(form.stock)     && form.stock     > 0 ? form.stock     : null)
    setOrDelete('auto_fulfill',      form.autoFulfill ? true : null)
    setOrDelete('auto_fulfill_note', form.autoFulfill && form.autoFulfillNote.trim() ? form.autoFulfillNote.trim() : null)

    // Strip legacy keys aggressively in case an old row still carries them.
    for (const k of [
      'image_url', 'gallery',
      'description_i18n', 'features_i18n', 'warning_i18n',
      'service_note_i18n', 'shipping_note_i18n', 'sku_label_i18n',
      'delivery_format', 'usage_url',
    ]) delete metadata[k]

    const metadataArg = Object.keys(metadata).length > 0 ? metadata : undefined

    // Parse the live input strings into cents. Either may be null = "unset".
    const priceUsdCents = inputTextToCents(form.priceUsdInput)
    const priceCnyCents = inputTextToCents(form.priceCnyInput)

    // Backend still requires a legacy priceCents + currency (notNull schema).
    // Pick whichever locale-specific price the admin filled in; prefer USD.
    const legacyCents = priceUsdCents ?? priceCnyCents ?? 0
    const legacyCurrency =
      priceUsdCents != null ? 'USD' :
      priceCnyCents != null ? 'CNY' : 'USD'

    try {
      // Trim once and normalize empties to null so PATCH actually clears
      // the field (undefined would be ignored by the server).
      const coverTagOut: string | null = form.coverTag.trim() ? form.coverTag.trim() : null
      const coverTagColorOut: CoverTagColor | null = form.coverTagColor || null
      if (form.id) {
        const payload: Partial<ProductCreateInput> = {
          sku: form.sku,                  // SKU is now editable on existing rows
          type: form.type,
          nameI18n: form.nameI18n,
          priceCents: legacyCents,
          currency: legacyCurrency,
          priceUsdCents,
          priceCnyCents,
          active: form.active,
          metadata: metadataArg,
          coverTag: coverTagOut,
          coverTagColor: coverTagColorOut,
        }
        await api.patchProduct(form.id, payload)
        toast.success(`${form.sku}`, t('products.toast.updated'))
      } else {
        await api.createProduct({
          sku: form.sku,
          type: form.type,
          nameI18n: form.nameI18n,
          priceCents: legacyCents,
          currency: legacyCurrency,
          priceUsdCents,
          priceCnyCents,
          active: form.active,
          metadata: metadataArg,
          coverTag: coverTagOut,
          coverTagColor: coverTagColorOut,
        })
        toast.success(`${form.sku}`, t('products.toast.created'))
      }
      setEditing(null)
      load()
    } catch (e) {
      toast.error(t('products.toast.saveFailed'), errorMessage(e))
    }
  }

  if (error) return <PageError msg={error}/>
  if (!products && busy) return <PageLoading/>

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('products.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('products.subtitle', { n: products?.length ?? 0 })}</p>
        </div>
        <Button onClick={() => setEditing(blankProduct())}>
          <Plus size={14} strokeWidth={2}/> {t('products.action.new')}
        </Button>
      </header>

      <Table>
        <THead>
          <Tr>
            <Th className="w-12"></Th>
            <Th>{t('products.col.sku')}</Th>
            <Th>{t('products.col.name')}</Th>
            <Th>{t('products.col.type')}</Th>
            <Th>{t('products.col.price')}</Th>
            <Th>{t('products.col.active')}</Th>
            <Th className="text-right pr-4">{t('products.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {products?.map((p) => {
            // 主图：优先 metadata.image_url（旧字段），fallback metadata.images[0]
            // （新版商品都用 images 数组）。
            const md = (p.metadata as any) || {}
            const img = (typeof md.image_url === 'string' && md.image_url)
              || (Array.isArray(md.images) && md.images.find((u: unknown) => typeof u === 'string' && u))
              || undefined
            return (
            <Tr key={p.id}>
              <Td>
                {img ? (
                  <img src={imageDisplayUrl(img)} alt="" className="h-8 w-8 rounded object-cover bg-muted"/>
                ) : (
                  <div className="h-8 w-8 rounded bg-muted/40 border border-dashed border-border flex items-center justify-center text-muted-foreground/60">
                    <ImageIcon size={12} strokeWidth={1.5}/>
                  </div>
                )}
              </Td>
              <Td className="font-mono text-xs">{p.sku}</Td>
              <Td className="font-medium">{p.nameI18n.zh ?? p.nameI18n.en ?? p.sku}</Td>
              <Td><Badge>{p.type}</Badge></Td>
              <Td><PriceCell p={p}/></Td>
              <Td>
                {p.active
                  ? <Badge tone="success">{t('status.active')}</Badge>
                  : <Badge tone="default">{t('status.inactive')}</Badge>}
              </Td>
              <Td className="text-right space-x-1.5 whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => setEditing(toEditing(p))}>{t('action.edit')}</Button>
                <Button size="sm" variant="outline" onClick={() => toggleActive(p)}>
                  {p.active ? t('action.disable') : t('action.enable')}
                </Button>
                <Button size="sm" variant="danger" onClick={() => setDeleting(p)}>{t('action.delete')}</Button>
              </Td>
            </Tr>
          )})}
        </TBody>
      </Table>

      {editing && (
        <ProductEditor
          value={editing}
          dict={dict}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save(editing)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('products.confirmDelete.title')}
        message={deleting ? t('products.confirmDelete.body', { sku: deleting.sku }) : ''}
        confirmLabel={t('products.confirmDelete.confirm')}
        cancelLabel={t('products.confirmDelete.cancel')}
        tone="danger"
        busy={deleteBusy}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

/** Two-line cell showing both locale prices. Falls back to "—" for unset. */
function PriceCell({ p }: { p: AdminProduct }) {
  // If only one is set, fall back to legacy priceCents on the matching side
  // so old products that only have priceCents still render something useful.
  const usd = p.priceUsdCents ?? (p.currency === 'USD' ? p.priceCents : null)
  const cny = p.priceCnyCents ?? (p.currency === 'CNY' ? p.priceCents : null)
  return (
    <div className="tabular-nums leading-tight">
      <div className={usd == null ? 'text-muted-foreground' : ''}>
        <span className="text-[10px] text-muted-foreground/70 mr-1">USD</span>
        {usd == null ? '—' : fmtMoney(usd, 'USD')}
      </div>
      <div className={cny == null ? 'text-muted-foreground' : ''}>
        <span className="text-[10px] text-muted-foreground/70 mr-1">CNY</span>
        {cny == null ? '—' : fmtMoney(cny, 'CNY')}
      </div>
    </div>
  )
}

interface Editing {
  id?: string
  sku: string
  type: 'plan' | 'physical' | 'topup'
  nameI18n: Record<string, string>
  /** Raw text the admin is typing into the price inputs. */
  priceUsdInput: string
  priceCnyInput: string
  /** "Official" / pre-discount prices. When set, the customer detail page
   *  shows the strike-through original next to the live price plus a "省 ¥N"
   *  badge auto-computed from the diff. Optional. */
  priceUsdOrigInput: string
  priceCnyOrigInput: string
  active: boolean
  /** Storefront filter category (rec/ai/api/...) — separate from `variant`. */
  cats: string[]
  /** Highlight this product in the storefront "推荐" tab. */
  featured: boolean

  // ─── New detail-page model ───────────────────────────────────
  /** Brand badge: "OpenAI" / "Anthropic" / "Cursor" / ... */
  brand: string
  /** Retail-style variant: 代充 (top-up) vs. 成品号 (ready). */
  variant: '' | 'topup' | 'ready'
  /** Per-month options when `variant === 'topup'`. Each option carries its own
   *  prices so admin can give discounts on longer durations. */
  topupOptions: TopupOption[]
  /** When the customer must provide an email. */
  emailRequired: 'never' | 'cart' | 'buy' | 'both'
  /** Up to 5 images. images[0] is the default cover. */
  images: string[]
  /** Free-form colored badges on the detail page near the title. */
  tags: ProductTag[]
  /** Short tagline shown under the product title on the detail page. */
  subtitleZh: string
  subtitleEn: string
  /** Markdown rich text shown on the "宝贝详情" tab (images via uploadImage). */
  descMdZh: string
  descMdEn: string
  /** References to shared content blocks — slugs of policy templates. */
  policyDelivery: string
  policyAddress: string
  policyNotice: string
  /** Display-only counters. */
  soldCount: number
  stock: number
  /** 自动发货：付款成功后系统自动将 autoFulfillNote 写入 fulfillment_note 并标记已发货。 */
  autoFulfill: boolean
  autoFulfillNote: string

  /** Cover-card chip in the storefront — short label + preset color. */
  coverTag: string
  coverTagColor: CoverTagColor | ''

  // Preserve any other metadata fields verbatim — we only touch known keys.
  metadataExtra: Record<string, unknown>
}

interface TopupOption {
  months: number
  /** null = inherit base priceCnyCents; same for USD. */
  priceCnyInput: string
  priceUsdInput: string
  /** Optional original / "official" price for striking through. */
  priceCnyOrigInput: string
  priceUsdOrigInput: string
}

interface ProductTag {
  label: string
  /** One of TAG_COLORS keys. */
  color: string
}

const TAG_COLORS: Array<{ key: string; bg: string; fg: string }> = [
  { key: 'gray',   bg: '#3f3f46', fg: '#e5e7eb' },
  { key: 'red',    bg: '#7f1d1d', fg: '#fecaca' },
  { key: 'orange', bg: '#7c2d12', fg: '#fed7aa' },
  { key: 'yellow', bg: '#713f12', fg: '#fef08a' },
  { key: 'green',  bg: '#14532d', fg: '#bbf7d0' },
  { key: 'blue',   bg: '#1e3a8a', fg: '#bfdbfe' },
  { key: 'purple', bg: '#581c87', fg: '#e9d5ff' },
]

/** cents → human input string. 1900 → "19.00", 75 → "0.75", null → "". */
function centsToInputText(cents: number | null): string {
  return cents == null ? '' : (cents / 100).toFixed(2)
}

/** Input text → cents, run only on save. "0.75" → 75, "" → null, garbage → null. */
function inputTextToCents(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100)
}

function blankProduct(): Editing {
  return {
    sku: '',
    type: 'plan',
    nameI18n: { en: '', zh: '' },
    priceUsdInput: '',
    priceCnyInput: '',
    priceUsdOrigInput: '',
    priceCnyOrigInput: '',
    active: true,
    cats: [],
    featured: false,
    brand: '',
    variant: '',
    topupOptions: [],
    emailRequired: 'never',
    images: [],
    tags: [],
    subtitleZh: '',
    subtitleEn: '',
    descMdZh: '',
    descMdEn: '',
    policyDelivery: '',
    policyAddress: '',
    policyNotice: '',
    soldCount: 0,
    stock: 0,
    coverTag: '',
    coverTagColor: '',
    autoFulfill: false,
    autoFulfillNote: '',
    metadataExtra: {},
  }
}

function toEditing(p: AdminProduct): Editing {
  const md = (p.metadata ?? {}) as Record<string, unknown>
  const {
    image_url, cat, featured,
    brand, variant, topup_options, email_required,
    images, tags, subtitle_i18n,
    price_orig_cny_cents, price_orig_usd_cents,
    description_md_i18n, policy_refs,
    sold_count, stock,
    // Strip the legacy fields so an admin saving an unchanged product doesn't
    // ship them back. We don't preserve them in metadataExtra either.
    description_i18n: _d, features_i18n: _f, warning_i18n: _w,
    service_note_i18n: _s, shipping_note_i18n: _sh, sku_label_i18n: _sl,
    delivery_format: _df, usage_url: _uu, gallery: _g,
    ...rest
  } = md as Record<string, any>

  const priceUsdCents = p.priceUsdCents ?? (p.currency === 'USD' ? p.priceCents : null)
  const priceCnyCents = p.priceCnyCents ?? (p.currency === 'CNY' ? p.priceCents : null)

  // Backward-compat: a single legacy `image_url` becomes images[0]; legacy
  // `gallery` rows fill the rest until we hit the 5-image cap.
  const imgs: string[] = []
  if (Array.isArray(images)) imgs.push(...images.filter((s): s is string => typeof s === 'string'))
  if (imgs.length === 0 && typeof image_url === 'string' && image_url) imgs.push(image_url)
  if (Array.isArray(_g)) for (const g of _g as string[]) {
    if (typeof g === 'string' && !imgs.includes(g) && imgs.length < 5) imgs.push(g)
  }

  const topupOptions: TopupOption[] = Array.isArray(topup_options)
    ? topup_options
        .filter((o: any) => o && typeof o.months === 'number')
        .map((o: any) => ({
          months: o.months,
          priceCnyInput: centsToInputText(typeof o.price_cny_cents === 'number' ? o.price_cny_cents : null),
          priceUsdInput: centsToInputText(typeof o.price_usd_cents === 'number' ? o.price_usd_cents : null),
          priceCnyOrigInput: centsToInputText(typeof o.price_orig_cny_cents === 'number' ? o.price_orig_cny_cents : null),
          priceUsdOrigInput: centsToInputText(typeof o.price_orig_usd_cents === 'number' ? o.price_orig_usd_cents : null),
        }))
    : []

  const tagList: ProductTag[] = Array.isArray(tags)
    ? tags.filter((x: any) => x && typeof x.label === 'string')
        .map((x: any) => ({ label: x.label, color: typeof x.color === 'string' ? x.color : 'gray' }))
    : []

  return {
    id: p.id,
    sku: p.sku,
    type: p.type,
    nameI18n: { en: p.nameI18n.en ?? '', zh: p.nameI18n.zh ?? '' },
    priceUsdInput: centsToInputText(priceUsdCents),
    priceCnyInput: centsToInputText(priceCnyCents),
    priceUsdOrigInput: centsToInputText(typeof price_orig_usd_cents === 'number' ? price_orig_usd_cents : null),
    priceCnyOrigInput: centsToInputText(typeof price_orig_cny_cents === 'number' ? price_orig_cny_cents : null),
    active: p.active,
    // 兼容历史：旧数据 metadata.cat 是 string，新版用 metadata.cats 数组。
    cats: Array.isArray((p.metadata as any)?.cats)
      ? ((p.metadata as any).cats as unknown[]).filter((x): x is string => typeof x === 'string')
      : (typeof cat === 'string' && cat ? [cat] : []),
    featured: featured === true,
    brand: typeof brand === 'string' ? brand : '',
    variant: variant === 'topup' || variant === 'ready' ? variant : '',
    topupOptions,
    emailRequired:
      (email_required === 'cart' || email_required === 'buy' || email_required === 'both')
        ? email_required : 'never',
    images: imgs.slice(0, 5),
    tags: tagList,
    subtitleZh: subtitle_i18n?.zh ?? '',
    subtitleEn: subtitle_i18n?.en ?? '',
    descMdZh: description_md_i18n?.zh ?? '',
    descMdEn: description_md_i18n?.en ?? '',
    policyDelivery: policy_refs?.delivery ?? '',
    policyAddress:  policy_refs?.address ?? '',
    policyNotice:   policy_refs?.notice ?? '',
    soldCount: typeof sold_count === 'number' ? sold_count : 0,
    stock:     typeof stock === 'number' ? stock : 0,
    coverTag: p.coverTag ?? '',
    coverTagColor: (p.coverTagColor ?? '') as CoverTagColor | '',
    autoFulfill: (md as any).auto_fulfill === true,
    autoFulfillNote: typeof (md as any).auto_fulfill_note === 'string' ? (md as any).auto_fulfill_note : '',
    metadataExtra: rest,
  }
}

function ProductEditor({
  value, dict, onChange, onCancel, onSave,
}: {
  value: Editing
  dict: DictGroups
  onChange: (next: Editing) => void
  onCancel: () => void
  onSave: () => void
}) {
  const { t } = useI18n()
  const set = <K extends keyof Editing>(k: K, v: Editing[K]) => onChange({ ...value, [k]: v })

  // canSave: at least one price input parses to a non-negative number.
  // We compute this by reusing the save-time parser so the rules match exactly.
  // For topup variant, prices live on per-month options instead of the four
  // top-level price inputs — require at least one option row.
  const isTopup = value.variant === 'topup'
  const canSave = isTopup
    ? value.topupOptions.length > 0
    : (inputTextToCents(value.priceUsdInput) != null ||
       inputTextToCents(value.priceCnyInput) != null)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-5 space-y-4 animate-[slideUp_0.18s_ease-out] max-h-[90vh] overflow-y-auto">
        <header className="flex items-center justify-between sticky top-0 bg-card z-10 -mx-5 -mt-5 px-5 pt-5 pb-3 border-b border-border">
          <h2 className="text-lg font-semibold tracking-tight">
            {value.id ? t('products.editor.titleEdit') : t('products.editor.titleNew')}
          </h2>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
            <X size={18}/>
          </button>
        </header>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">{t('products.field.sku')}</label>
            <Input
              value={value.sku}
              onChange={(e) => set('sku', e.target.value.toUpperCase())}
              placeholder="CLAUDE-PRO"
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">{t('products.field.type')}</label>
            <DictSelect
              dict={dict}
              dictKey="variant"
              value={value.variant}
              onChange={(v) => {
                // Keep product.type (DB enum: plan|physical|topup) in sync with
                // the selected dict variant. The shop API still routes on type,
                // but the admin no longer chooses it directly. Rules:
                //   variant=topup → type=topup (so /shop/products?type=topup works)
                //   variant=ready → type=plan  (default for SaaS-style entries)
                //   anything else → leave type unchanged (legacy rows keep their type)
                const next: Partial<Editing> = { variant: v as Editing['variant'] }
                if (v === 'topup') next.type = 'topup'
                else if (v === 'ready') next.type = 'plan'
                onChange({ ...value, ...next })
              }}
              emptyLabel="—"
            />
          </div>
          {!isTopup && (
            <>
              <div>
                <label className="text-xs text-muted-foreground">{t('products.field.priceUsd')}</label>
                <Input type="number" min={0} step={0.01}
                  value={value.priceUsdInput}
                  onChange={(e) => set('priceUsdInput', e.target.value)}
                  placeholder="0.00"/>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t('products.field.priceCny')}</label>
                <Input type="number" min={0} step={0.01}
                  value={value.priceCnyInput}
                  onChange={(e) => set('priceCnyInput', e.target.value)}
                  placeholder="0.00"/>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t('products.field.priceUsdOrig')}</label>
                <Input type="number" min={0} step={0.01}
                  value={value.priceUsdOrigInput}
                  onChange={(e) => set('priceUsdOrigInput', e.target.value)}
                  placeholder="—"/>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t('products.field.priceCnyOrig')}</label>
                <Input type="number" min={0} step={0.01}
                  value={value.priceCnyOrigInput}
                  onChange={(e) => set('priceCnyOrigInput', e.target.value)}
                  placeholder="—"/>
              </div>
              <div className="col-span-2 text-[11px] text-muted-foreground/80 -mt-1">
                {t('products.field.priceHelp')}
              </div>
            </>
          )}

          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">{t('products.field.nameEn')}</label>
            <Input
              value={value.nameI18n.en ?? ''}
              onChange={(e) => set('nameI18n', { ...value.nameI18n, en: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">{t('products.field.nameZh')}</label>
            <Input
              value={value.nameI18n.zh ?? ''}
              onChange={(e) => set('nameI18n', { ...value.nameI18n, zh: e.target.value })}
            />
          </div>
          <ImagesField
            value={value.images}
            sku={value.sku}
            onChange={(imgs) => set('images', imgs)}
          />
          <div>
            <label className="text-xs text-muted-foreground">{t('products.field.category')}</label>
            <DictMultiSelect
              dict={dict}
              dictKey="cat"
              value={value.cats}
              onChange={(v) => set('cats', v)}
              placeholder={t('products.field.category')}
            />
          </div>
          <label className="flex items-end gap-2 text-sm pb-1.5">
            <input
              type="checkbox"
              checked={value.featured}
              onChange={(e) => set('featured', e.target.checked)}
            />
            {t('products.field.featured')}
          </label>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={value.active}
              onChange={(e) => set('active', e.target.checked)}
            />
            {t('products.field.active')}
          </label>

          {/* Cover-card chip — short label + preset color shown at top-right
              of the storefront product card. Empty label = no chip rendered.
              Independent from `featured` (which still drives the storefront
              "推荐" tab membership). */}
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground">{t('products.field.coverTag')}</label>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-end">
              <Input
                value={value.coverTag}
                onChange={(e) => set('coverTag', e.target.value.slice(0, 20))}
                placeholder={t('products.field.coverTagPlaceholder')}
                maxLength={20}
              />
              <CoverTagColorSwatches
                value={value.coverTagColor}
                onChange={(v) => set('coverTagColor', v)}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              {t('products.field.coverTagHint')}
            </p>
          </div>
        </div>

        <DetailFields value={value} dict={dict} set={set}/>

        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-card -mx-5 -mb-5 px-5 py-3 border-t border-border">
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
          <Button onClick={onSave} disabled={!canSave}>{t('action.save')}</Button>
        </div>
      </div>
    </div>
  )
}


// ─── Cover-tag color picker ───────────────────────────────────────
//
// 8 preset swatches + 1 "无" choice for clearing the color. Background
// hex shown in the swatch is a dim approximation; the actual rendered
// chip on the storefront pulls from `marketing.css` `.shop2-tag--<key>`
// rules so a color tweak there propagates without admin redeploy.

const TAG_SWATCH_BG: Record<CoverTagColor, string> = {
  orange: '#c2410c',
  red:    '#b91c1c',
  green:  '#15803d',
  blue:   '#1d4ed8',
  yellow: '#a16207',
  grey:   '#52525b',
  purple: '#7e22ce',
  pink:   '#be185d',
}

function CoverTagColorSwatches({
  value,
  onChange,
}: {
  value: CoverTagColor | ''
  onChange: (v: CoverTagColor | '') => void
}) {
  const baseSwatch = 'h-6 w-6 rounded border border-border transition-all hover:scale-110 cursor-pointer flex items-center justify-center'
  const activeRing = 'ring-2 ring-offset-1 ring-offset-card ring-primary'
  return (
    <div className="flex items-center gap-1.5 pb-1.5">
      {/* "无" — clears the chip color (chip won't render until a color is set) */}
      <button
        type="button"
        onClick={() => onChange('')}
        title="无"
        className={`${baseSwatch} bg-transparent text-muted-foreground text-[10px] ${value === '' ? activeRing : ''}`}
      >
        ✕
      </button>
      {COVER_TAG_COLORS.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          title={k}
          className={`${baseSwatch} ${value === k ? activeRing : ''}`}
          style={{ background: TAG_SWATCH_BG[k] }}
        />
      ))}
    </div>
  )
}

// ─── Images (up to 5; [0] is cover) ──────────────────────────────

const MAX_IMAGES = 5

function ImagesField({
  value, sku, onChange,
}: {
  value: string[]
  sku: string
  onChange: (next: string[]) => void
}) {
  const { t } = useI18n()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function pickFiles(files: FileList) {
    setBusy(true); setErr(null)
    try {
      const prefix = `products/${sku ? sku.toLowerCase() : 'misc'}`
      const next = [...value]
      for (const f of Array.from(files)) {
        if (next.length >= MAX_IMAGES) break
        const r = await api.uploadImage(f, prefix)
        next.push(r.url)
      }
      onChange(next)
    } catch (e) { setErr(errorMessage(e)) }
    finally { setBusy(false) }
  }

  function makeCover(i: number) {
    if (i === 0) return
    const next = [...value]
    const [picked] = next.splice(i, 1)
    next.unshift(picked)
    onChange(next)
  }

  return (
    <div className="col-span-2">
      <label className="text-xs text-muted-foreground">
        {t('products.field.images')} <span className="text-muted-foreground/70">({value.length}/{MAX_IMAGES})</span>
      </label>
      <div className="mt-1 flex flex-wrap gap-2">
        {value.map((url, i) => (
          <div key={url + i} className="relative h-20 w-20 rounded-md border border-border overflow-hidden bg-muted group">
            <img src={imageDisplayUrl(url)} alt="" className="h-full w-full object-cover"/>
            {i === 0 && (
              <div className="absolute top-1 left-1 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-500/90 text-white">
                <Star size={9} strokeWidth={2.5} fill="currentColor"/> {t('products.field.cover')}
              </div>
            )}
            <button type="button"
              className="absolute top-1 right-1 bg-black/70 text-white text-[11px] px-1.5 py-0.5 rounded hover:bg-black"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              aria-label="Remove">×</button>
            {i !== 0 && (
              <button type="button"
                className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded hover:bg-black opacity-0 group-hover:opacity-100"
                onClick={() => makeCover(i)}
                title={t('products.field.makeCover')}>
                <Star size={11} strokeWidth={2}/>
              </button>
            )}
          </div>
        ))}
        {value.length < MAX_IMAGES && (
          <button type="button"
            className="h-20 w-20 rounded-md border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/30 disabled:opacity-50"
            disabled={busy}
            onClick={() => inputRef.current?.click()}>
            {busy
              ? <Loader2 size={16} className="animate-spin"/>
              : <><Plus size={16} strokeWidth={2}/><span className="text-[10px] mt-1">{t('products.field.upload')}</span></>}
          </button>
        )}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground/80">{t('products.field.imagesHint')}</div>
      {err && <div className="text-[11px] text-danger">{err}</div>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        multiple
        className="hidden"
        onChange={(e) => {
          const fs = e.target.files
          if (fs && fs.length > 0) pickFiles(fs)
          e.currentTarget.value = ''
        }}
      />
    </div>
  )
}

// ─── DetailFields — collapsible advanced section ────────────────

function DetailFields({
  value, dict, set,
}: {
  value: Editing
  dict: DictGroups
  set: <K extends keyof Editing>(k: K, v: Editing[K]) => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState<boolean>(() => hasAnyDetail(value))

  return (
    <div className="border-t border-border pt-3">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-foreground/80">
        <span>{open ? '▾' : '▸'}</span>
        {t('products.field.detailSection')}
        <span className="text-xs text-muted-foreground">{t('products.field.detailHint')}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <BrandField dict={dict} value={value.brand} onChange={(v) => set('brand', v)}/>
            <EmailRequiredField value={value.emailRequired} onChange={(v) => set('emailRequired', v)}/>
            <div>
              <label className="text-xs text-muted-foreground">{t('products.field.soldCount')}</label>
              <Input type="number" min={0} value={value.soldCount || ''}
                onChange={(e) => set('soldCount', Math.max(0, parseInt(e.target.value) || 0))} placeholder="0"/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('products.field.stock')}</label>
              <Input type="number" min={0} value={value.stock || ''}
                onChange={(e) => set('stock', Math.max(0, parseInt(e.target.value) || 0))} placeholder="0"/>
            </div>
          </div>

          {value.variant === 'topup' && (
            <TopupOptionsField value={value.topupOptions} onChange={(v) => set('topupOptions', v)}/>
          )}

          {/* ── 发货模式 ── */}
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto-fulfill-toggle"
                checked={value.autoFulfill}
                onChange={(e) => set('autoFulfill', e.target.checked)}
                className="h-4 w-4 rounded border-border accent-brand"
              />
              <label htmlFor="auto-fulfill-toggle" className="text-sm font-medium cursor-pointer select-none">
                {t('products.field.autoFulfill')}
              </label>
              <span className="text-xs text-muted-foreground">{t('products.field.autoFulfillHint')}</span>
            </div>
            {value.autoFulfill && (
              <div>
                <label className="text-xs text-muted-foreground block mb-1">{t('products.field.autoFulfillNote')}</label>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono resize-y min-h-[80px]"
                  placeholder={t('products.field.autoFulfillNotePlaceholder')}
                  value={value.autoFulfillNote}
                  onChange={(e) => set('autoFulfillNote', e.target.value)}
                />
              </div>
            )}
          </div>

          <TagsField value={value.tags} onChange={(v) => set('tags', v)}/>

          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">{t('products.field.subtitleZh')}</label>
              <textarea
                value={value.subtitleZh}
                onChange={(e) => set('subtitleZh', e.target.value)}
                rows={2}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                placeholder="由车主统一开通，发送至你提供的邮箱…"/>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t('products.field.subtitleEn')}</label>
              <textarea
                value={value.subtitleEn}
                onChange={(e) => set('subtitleEn', e.target.value)}
                rows={2}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                placeholder="Operator-provisioned. Plus benefits delivered to the email you choose…"/>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <MarkdownField
              label={t('products.field.descMdZh')}
              sku={value.sku}
              value={value.descMdZh}
              onChange={(v) => set('descMdZh', v)}/>
            <MarkdownField
              label={t('products.field.descMdEn')}
              sku={value.sku}
              value={value.descMdEn}
              onChange={(v) => set('descMdEn', v)}/>
          </div>

          <div className="grid grid-cols-1 gap-3 border-t border-border pt-3">
            <div className="text-xs font-medium text-foreground">{t('products.field.policyRefs')}</div>
            <PolicyRefField type="delivery" value={value.policyDelivery} onChange={(v) => set('policyDelivery', v)}/>
            <PolicyRefField type="address"  value={value.policyAddress}  onChange={(v) => set('policyAddress', v)}/>
            <PolicyRefField type="notice"   value={value.policyNotice}   onChange={(v) => set('policyNotice', v)}/>
          </div>
        </div>
      )}
    </div>
  )
}

function hasAnyDetail(v: Editing): boolean {
  return !!(
    v.brand || v.variant || v.emailRequired !== 'never' ||
    v.images.length > 0 || v.tags.length > 0 ||
    v.descMdZh || v.descMdEn ||
    v.policyDelivery || v.policyAddress || v.policyNotice ||
    v.soldCount > 0 || v.stock > 0 || v.topupOptions.length > 0
  )
}

// ─── Brand (dict-driven) ───────────────────────────────────────

function BrandField({ dict, value, onChange }: { dict: DictGroups; value: string; onChange: (v: string) => void }) {
  const { t } = useI18n()
  return (
    <div>
      <label className="text-xs text-muted-foreground">{t('products.field.brand')}</label>
      <DictSelect dict={dict} dictKey="brand" value={value} onChange={onChange} emptyLabel="—"/>
    </div>
  )
}

// ─── Generic dict-backed <select> ─────────────────────────────────────────
// Reads dict[dictKey] (already filtered to active=1) and renders one option per
// row. If the current value isn't in the dict — typically a row with a code
// that has since been deactivated, or a category created before dictionaries
// existed — we add a sticky "(legacy: X)" option so the editor can still load
// and save the product without silently mutating its metadata.
function DictSelect({
  dict, dictKey, value, onChange, emptyLabel,
}: {
  dict: DictGroups
  dictKey: string
  value: string
  onChange: (v: string) => void
  emptyLabel: string
}) {
  const items = dict[dictKey] ?? []
  const known = items.some((i) => i.code === value)
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
    >
      <option value="">{emptyLabel}</option>
      {items.map((i) => (
        <option key={i.id} value={i.code}>
          {i.labelZh}{i.labelEn ? ` · ${i.labelEn}` : ''}
        </option>
      ))}
      {value && !known && (
        <option value={value}>{`(legacy: ${value})`}</option>
      )}
    </select>
  )
}

// 多选 dict 字段（用于商品分类等需要打多 tag 的场景）。
// 未选中时显示 placeholder；选中后显示 chip 列表，可单独 × 移除。
// 点 chip 区域唤出 popover 勾选 / 取消。
function DictMultiSelect({
  dict, dictKey, value, onChange, placeholder,
}: {
  dict: DictGroups
  dictKey: string
  value: string[]
  onChange: (v: string[]) => void
  placeholder: string
}) {
  const [open, setOpen] = useState(false)
  const items = dict[dictKey] ?? []
  const codeToLabel = new Map(items.map((i) => [i.code, i.labelZh + (i.labelEn ? ` · ${i.labelEn}` : '')]))
  const toggle = (code: string) => {
    if (value.includes(code)) onChange(value.filter((c) => c !== code))
    else onChange([...value, code])
  }
  return (
    <div className="relative">
      <button
        type="button"
        className="min-h-9 w-full rounded-md border border-border bg-card px-2 py-1 text-left text-sm flex flex-wrap gap-1 items-center"
        onClick={() => setOpen((o) => !o)}
      >
        {value.length === 0 ? (
          <span className="text-muted-foreground px-1">{placeholder}</span>
        ) : (
          value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded bg-accent/40 px-2 py-0.5 text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              {codeToLabel.get(code) ?? code}
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => toggle(code)}
              >×</button>
            </span>
          ))
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)}/>
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg p-1">
            {items.length === 0 && (
              <div className="px-2 py-2 text-xs text-muted-foreground">— empty —</div>
            )}
            {items.map((i) => (
              <label
                key={i.id}
                className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-accent/30"
              >
                <input
                  type="checkbox"
                  checked={value.includes(i.code)}
                  onChange={() => toggle(i.code)}
                />
                <span>{i.labelZh}{i.labelEn ? <span className="text-muted-foreground"> · {i.labelEn}</span> : null}</span>
              </label>
            ))}
            {/* Sticky 显示历史已选但已停用的 code，避免静默丢值 */}
            {value.filter((c) => !items.some((i) => i.code === c)).map((c) => (
              <label key={c} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded">
                <input type="checkbox" checked readOnly/>
                <span className="text-muted-foreground">(legacy: {c})</span>
                <button type="button" className="ml-auto text-xs text-muted-foreground hover:text-foreground" onClick={() => toggle(c)}>×</button>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Topup options (per-month rows with own prices) ────────────

function TopupOptionsField({
  value, onChange,
}: {
  value: TopupOption[]
  onChange: (v: TopupOption[]) => void
}) {
  const { t } = useI18n()
  return (
    <div className="border border-border rounded-md p-3 space-y-2">
      <div className="text-xs font-medium text-foreground">{t('products.field.topupOptions')}</div>
      <div className="text-[11px] text-muted-foreground/80">{t('products.field.topupOptionsHint')}</div>

      <div className="grid grid-cols-[60px_1fr_1fr_1fr_1fr_auto] gap-1.5 items-center text-[10px] text-muted-foreground/80 px-1">
        <span>{t('products.field.topupColMonths')}</span>
        <span>CNY</span>
        <span>{t('products.field.topupColCnyOrig')}</span>
        <span>USD</span>
        <span>{t('products.field.topupColUsdOrig')}</span>
        <span/>
      </div>

      <div className="space-y-1.5">
        {value.map((row, i) => (
          <div key={i} className="grid grid-cols-[60px_1fr_1fr_1fr_1fr_auto] gap-1.5 items-center">
            <Input type="number" min={1} value={row.months}
              onChange={(e) => onChange(value.map((r, idx) => idx === i ? { ...r, months: Math.max(1, parseInt(e.target.value) || 0) } : r))}
              placeholder="1"/>
            <Input type="number" min={0} step={0.01} value={row.priceCnyInput}
              onChange={(e) => onChange(value.map((r, idx) => idx === i ? { ...r, priceCnyInput: e.target.value } : r))}
              placeholder="¥"/>
            <Input type="number" min={0} step={0.01} value={row.priceCnyOrigInput}
              onChange={(e) => onChange(value.map((r, idx) => idx === i ? { ...r, priceCnyOrigInput: e.target.value } : r))}
              placeholder="—"/>
            <Input type="number" min={0} step={0.01} value={row.priceUsdInput}
              onChange={(e) => onChange(value.map((r, idx) => idx === i ? { ...r, priceUsdInput: e.target.value } : r))}
              placeholder="$"/>
            <Input type="number" min={0} step={0.01} value={row.priceUsdOrigInput}
              onChange={(e) => onChange(value.map((r, idx) => idx === i ? { ...r, priceUsdOrigInput: e.target.value } : r))}
              placeholder="—"/>
            <Button size="sm" variant="ghost" type="button"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}>
              <X size={13}/>
            </Button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5 pt-1">
        {[1, 3, 6, 12].map((m) => (
          <Button key={m} size="sm" variant="outline" type="button"
            disabled={value.some((r) => r.months === m)}
            onClick={() => onChange([...value, { months: m, priceCnyInput: '', priceUsdInput: '', priceCnyOrigInput: '', priceUsdOrigInput: '' }])}>
            <Plus size={11}/> {m} {t('products.field.topupMonthsSuffix')}
          </Button>
        ))}
      </div>
    </div>
  )
}

// ─── Email-required policy ─────────────────────────────────────

// 邮箱必填字段：两个 checkbox（加购必填 / 购买必填）独立勾选，组合派生
// 单一 string 存到 metadata.email_required：'never' | 'cart' | 'buy' | 'both'
// 客户端按这 4 个值分支即可（购买路径 !== 'never' 已涵盖 cart/buy/both）。
function EmailRequiredField({
  value, onChange,
}: {
  value: Editing['emailRequired']
  onChange: (v: Editing['emailRequired']) => void
}) {
  const { t } = useI18n()
  const cartChecked = value === 'cart' || value === 'both'
  const buyChecked  = value === 'buy'  || value === 'both'
  function flip(which: 'cart' | 'buy', next: boolean) {
    const c = which === 'cart' ? next : cartChecked
    const b = which === 'buy'  ? next : buyChecked
    if (c && b) onChange('both')
    else if (c) onChange('cart')
    else if (b) onChange('buy')
    else onChange('never')
  }
  return (
    <div>
      <label className="text-xs text-muted-foreground">{t('products.field.emailRequired')}</label>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={cartChecked}
            onChange={(e) => flip('cart', e.target.checked)}
          />
          <span>{t('products.field.emailCart')}</span>
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={buyChecked}
            onChange={(e) => flip('buy', e.target.checked)}
          />
          <span>{t('products.field.emailBuy')}</span>
        </label>
        {!cartChecked && !buyChecked && (
          <span className="text-xs text-muted-foreground">{t('products.field.emailNever')}</span>
        )}
      </div>
    </div>
  )
}

// ─── Tags ──────────────────────────────────────────────────────

function TagsField({
  value, onChange,
}: {
  value: ProductTag[]
  onChange: (v: ProductTag[]) => void
}) {
  const { t } = useI18n()
  return (
    <div className="border border-border rounded-md p-3 space-y-2">
      <div className="text-xs font-medium text-foreground">{t('products.field.tags')}</div>
      <div className="space-y-1.5">
        {value.map((tag, i) => {
          const palette = TAG_COLORS.find((c) => c.key === tag.color) ?? TAG_COLORS[0]!
          return (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-1.5 items-center">
              <Input value={tag.label} placeholder="渠道非自产"
                onChange={(e) => onChange(value.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}/>
              <div className="flex gap-0.5">
                {TAG_COLORS.map((c) => (
                  <button key={c.key} type="button"
                    className="h-7 w-7 rounded border-2 transition-all"
                    style={{
                      background: c.bg,
                      borderColor: c.key === tag.color ? c.fg : 'transparent',
                    }}
                    onClick={() => onChange(value.map((x, idx) => idx === i ? { ...x, color: c.key } : x))}
                    aria-label={c.key}/>
                ))}
              </div>
              <Button size="sm" variant="ghost" type="button"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}>
                <X size={13}/>
              </Button>
            </div>
          )
        })}
        <Button size="sm" variant="outline" type="button"
          onClick={() => onChange([...value, { label: '', color: 'gray' }])}>
          <Plus size={11}/> {t('products.field.addTag')}
        </Button>
      </div>
    </div>
  )
}

// ─── Markdown editor (textarea + image upload helper) ──────────

function MarkdownField({
  label, value, sku, onChange,
}: {
  label: string
  value: string
  sku: string
  onChange: (v: string) => void
}) {
  const { t } = useI18n()
  const taRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const previewHtml = useMemo(
    () => (showPreview ? renderMarkdown(value) : ''),
    [showPreview, value],
  )

  function insertAtCursor(text: string) {
    const ta = taRef.current
    if (!ta) { onChange(value + text); return }
    const start = ta.selectionStart ?? value.length
    const end = ta.selectionEnd ?? value.length
    const next = value.slice(0, start) + text + value.slice(end)
    onChange(next)
    requestAnimationFrame(() => {
      ta.focus()
      ta.selectionStart = ta.selectionEnd = start + text.length
    })
  }

  async function pickImage(file: File) {
    setBusy(true)
    try {
      const prefix = `products/${sku ? sku.toLowerCase() : 'misc'}/desc`
      const r = await api.uploadImage(file, prefix)
      insertAtCursor(`\n\n![](${r.url})\n\n`)
    } finally { setBusy(false) }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs text-muted-foreground">{label}</label>
        <div className="flex gap-1">
          <Button
            size="sm" variant="outline" type="button"
            onClick={() => setShowPreview((v) => !v)}
            title="切换 markdown 预览"
          >
            <Eye size={11}/>
            {showPreview ? '隐藏预览' : '预览'}
          </Button>
          <Button size="sm" variant="outline" type="button" onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? <Loader2 size={11} className="animate-spin"/> : <ImageIcon size={11}/>}
            {t('products.field.markdownInsertImage')}
          </Button>
        </div>
      </div>
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono"
        placeholder={'## 关于这个商品\n\n这是一段描述\n\n- 要点 1\n- 要点 2\n\n![alt](url)'}
      />
      {showPreview && (
        <div className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2">
          <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">预览</div>
          <div
            className="markdown-preview text-xs leading-relaxed"
            // SAFE: renderMarkdown 内部已 HTML-escape 所有字符再加 markup，stray <script> 不会执行
            dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="italic text-muted-foreground">（暂无内容）</p>' }}
          />
          <style>{`
            .markdown-preview h2 { font-size: 14px; font-weight: 600; margin: 0.6em 0 0.3em; }
            .markdown-preview h3 { font-size: 13px; font-weight: 600; margin: 0.5em 0 0.25em; }
            .markdown-preview h4 { font-size: 12.5px; font-weight: 600; margin: 0.4em 0 0.2em; }
            .markdown-preview p  { margin: 0.4em 0; }
            .markdown-preview ul, .markdown-preview ol { margin: 0.3em 0; padding-left: 1.4em; }
            .markdown-preview li { margin: 0.15em 0; }
            .markdown-preview code { background: var(--muted); padding: 0.05em 0.3em; border-radius: 3px; font-size: 0.9em; }
            .markdown-preview strong { font-weight: 600; }
            .markdown-preview em { font-style: italic; }
            .markdown-preview a { color: var(--primary); text-decoration: underline; }
            .markdown-preview hr { border: 0; border-top: 1px solid var(--border); margin: 0.8em 0; }
            .markdown-preview img { max-width: 100%; height: auto; border-radius: 4px; margin: 0.4em 0; }
          `}</style>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) pickImage(f); e.currentTarget.value = '' }}/>
    </div>
  )
}

// ─── Policy template ref selector ──────────────────────────────

function PolicyRefField({
  type, value, onChange,
}: {
  /** The slot the picker belongs to — 'delivery' | 'address' | 'notice'.
   *  Used only as the label; the dropdown now lists templates of ALL types
   *  so the same policy can be cross-mounted in any slot. The storefront
   *  renderer dispatches by the picked template's own type, not the slot. */
  type: PolicyTemplateType
  value: string
  onChange: (v: string) => void
}) {
  const { t } = useI18n()
  const [list, setList] = useState<PolicyTemplateRow[] | null>(null)

  useEffect(() => {
    // No `type` filter — fetch every active policy template so admin can
    // pick freely.
    api.listPolicyTemplates().then((r) => setList(r.templates)).catch(() => setList([]))
  }, [])

  // Group options by type so the dropdown is scannable when there are many
  // templates. Within each group, sort by title for deterministic order.
  const groups = useMemo(() => {
    const byType: Record<PolicyTemplateType, PolicyTemplateRow[]> = {
      delivery: [], address: [], notice: [],
    }
    for (const tpl of list ?? []) byType[tpl.type]?.push(tpl)
    for (const arr of Object.values(byType)) {
      arr.sort((a, b) => (a.titleI18n.zh ?? a.slug).localeCompare(b.titleI18n.zh ?? b.slug))
    }
    return byType
  }, [list])

  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 items-center">
      <label className="text-xs text-muted-foreground">{t(`policies.type.${type}` as any)}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm">
        <option value="">— {t('products.field.policyNone')} —</option>
        {(['delivery', 'address', 'notice'] as const).flatMap((g) => {
          const items = groups[g] ?? []
          if (items.length === 0) return []
          return [
            <optgroup key={g} label={t(`policies.type.${g}` as any)}>
              {items.map((tpl) => (
                <option key={tpl.id} value={tpl.slug}>
                  {tpl.titleI18n.zh ?? tpl.titleI18n.en ?? tpl.slug} ({tpl.slug})
                </option>
              ))}
            </optgroup>,
          ]
        })}
      </select>
    </div>
  )
}
